/**
 * LICITARS 3.0 - Dashboard V3
 * Exact reproduction of licitars-3-0/Dashboard.jsx with navy/silver theme
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { processApi } from "@/services/api";
import { PROCESS_STATUS, getProcessStatusLabel } from "@/constants/process-status";
import { QuickActionsCard } from "@/components/dashboard/QuickActionsCard";
import { ActiveProcessesCard } from "@/components/dashboard/ActiveProcessesCard";
// import { ApprovalBox } from "@/components/dashboard/ApprovalBox";
// import { CreditBalance } from "@/components/dashboard/CreditBalance";
// import { UpcomingDeadlinesCard } from "@/components/dashboard/UpcomingDeadlinesCard";

const DashboardV3: React.FC = () => {
  const { user } = useAuth();
  // Fetch all processes from API
  const { data: processResponse, isLoading } = useQuery({
    queryKey: ["processes"],
    queryFn: async () => {
      const response = await processApi.list();
      // Handle both array and paginated response
      if (Array.isArray(response)) {
        return { results: response, count: response.length };
      }
      return response;
    },
    enabled: !!user, // Only fetch when user is authenticated
  });

  // Filter and map active processes (ABERTO or EM_ANDAMENTO)
  const activeProcesses = React.useMemo(() => {
    const allProcesses = processResponse?.results || [];
    
    return allProcesses
      .filter((process) => {
        const statusUpper = process.status?.toUpperCase();
        return (
          statusUpper === PROCESS_STATUS.ABERTO ||
          statusUpper === PROCESS_STATUS.EM_ANDAMENTO
        );
      })
      .map((process) => ({
        id: String(process.id),
        object_summary: process.object || process.description || 'Processo sem título',
        current_stage: getProcessStatusLabel(process.status), // User-friendly status label
        status: process.status,
        progress_percentage: undefined, // TODO: Calculate from ProcessStage data
        deadline_date: undefined,
        urgency_level: undefined, // TODO: Calculate based on deadline proximity
        process_number: process.code,
        created_at: process.created_at,
      }));
  }, [processResponse]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-navy-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // TODO: Replace with real role detection
  const isGestor = user.role === "gestor" || user.role === "admin" || user.role === "master";

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Olá, {user.username?.split(' ')[0]}! 👋
          </h1>
          <p className="text-muted-foreground">
            {isGestor ? 'Aqui está o que precisa da sua atenção hoje' : 'Veja seus processos e acompanhe o progresso'}
          </p>
        </div>

        {/* TODO: Implement the back-end and integrate it with the component below. */}
        {/* Top Row - Approval Box (Gestor only) + Credit Balance */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {isGestor && pendingDocuments.length > 0 && (
            <ApprovalBox pendingCount={pendingDocuments.length} />
          )}
          <CreditBalance
            currentCredits={client.current_credits}
            monthlyCredits={client.monthly_credits}
            renewalDay={client.renewal_day}
            consumptionData={consumptionData}
            hasPendingDocs={isGestor && pendingDocuments.length > 0}
          />
        </div> */}
        {/* Quick Actions */}
        <QuickActionsCard />
        {/* Bottom Row - Active Processes */}
        <ActiveProcessesCard processes={activeProcesses} />
        {/* TODO: Implement the back-end and integrate it with the component below. */}
        {/* <UpcomingDeadlinesCard deadlines={upcomingDeadlines} /> */}
      </div>
    </div>
  );
};

export default DashboardV3;
